import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { formatDistanceToNow, Locale } from "date-fns";
// Import date-fns locales
import { enUS, de, es, fr, ar, zhCN, sl } from "date-fns/locale";
import { Globe, Star, StarIcon, Trash2, Lock } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/hooks/useLanguage";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useState, useEffect } from "react";
import { useFeatureLimits } from "@/hooks/useFeatureLimits";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { logger } from "@/lib/logger";

type Story = {
  id: string;
  title: string;
  genre: string[];
  created_at: string;
  finished_at: string | null;
  is_public?: boolean;
  user_id?: string;
  average_rating?: number | null;
  author?: string;
};

interface StoryCardProps {
  story: Story;
  isCompleted?: boolean;
  onStoryClick: (storyId: string, isCompleted: boolean) => void;
  onStoryDeleted?: () => void;
}

const StoryCard = ({ story, isCompleted = false, onStoryClick, onStoryDeleted }: StoryCardProps) => {
  const { t, language } = useLanguage(); // Get current language code
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [isUpgradeDialogOpen, setIsUpgradeDialogOpen] = useState(false);
  const [isPublic, setIsPublic] = useState(story.is_public);
  const [userRating, setUserRating] = useState<number | null>(null);
  const [isRating, setIsRating] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const { isFeatureEnabled } = useFeatureLimits();
  
  // Map language codes to date-fns locales
  const localeMap: Record<string, Locale> = {
    en: enUS,
    de: de,
    es: es,
    fr: fr,
    ar: ar,
    zh: zhCN,
    sl: sl
  };
  
  // Get the appropriate locale for current language
  const locale = localeMap[language] || enUS;
  
  const formattedDate = formatDistanceToNow(new Date(story.created_at), {
    addSuffix: true,
    locale: locale // Use the locale based on current language
  });

  const isCurrentUserStory = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    return user?.id === story.user_id;
  };

  const handleDelete = async () => {
    try {
      const { error } = await supabase
        .from("stories")
        .delete()
        .eq("id", story.id);

      if (error) throw error;

      if (onStoryDeleted) {
        onStoryDeleted();
      }

      setIsOpen(false);
      
      toast({
        title: t('dashboard.deleteStory.success'),
        description: t('dashboard.deleteStory.successMessage'),
      });
    } catch (error) {
      logger.error('Error deleting story:', error);
      toast({
        variant: "destructive",
        title: t('dashboard.deleteStory.error'),
        description: t('dashboard.deleteStory.errorMessage'),
      });
    }
  };

  const togglePublic = async (e: React.MouseEvent) => {
    e.stopPropagation();
    
    // Check if user has access to the make_public feature
    if (!isFeatureEnabled('story_features', 'make_public')) {
      setIsUpgradeDialogOpen(true);
      return;
    }
    
    try {
      const { error } = await supabase
        .from("stories")
        .update({ is_public: !isPublic })
        .eq("id", story.id);

      if (error) throw error;

      setIsPublic(!isPublic);
      toast({
        title: !isPublic ? t('dashboard.storyVisibility.madePublic') : t('dashboard.storyVisibility.madePrivate'),
        description: !isPublic 
          ? t('dashboard.storyVisibility.publicDescription')
          : t('dashboard.storyVisibility.privateDescription'),
      });
    } catch (error) {
      logger.error('Error toggling story visibility:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update story visibility",
      });
    }
  };

  const handleRate = async (rating: number) => {
    if (isRating) return;
    setIsRating(true);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { error } = await supabase
        .from('story_ratings')
        .upsert(
          {
            story_id: story.id,
            rating,
            user_id: user.id
          },
          {
            onConflict: 'story_id,user_id'
          }
        );

      if (error) throw error;

      // Fetch the updated average rating
      const { data: updatedStory, error: storyError } = await supabase
        .from('stories')
        .select('average_rating')
        .eq('id', story.id)
        .single();

      if (storyError) throw storyError;

      // Update local state
      setUserRating(rating);
      if (updatedStory) {
        story.average_rating = updatedStory.average_rating;
      }

      toast({
        title: t('dashboard.rating.submitted'),
        description: t('dashboard.rating.thankYou'),
      });
    } catch (error) {
      logger.error('Error rating story:', error);
      toast({
        variant: "destructive",
        title: t('dashboard.error'),
        description: t('dashboard.rating.failed'),
      });
    } finally {
      setIsRating(false);
    }
  };

  useEffect(() => {
    isCurrentUserStory().then(result => setIsOwner(result));

    // Load user's rating if they've rated this story
    const loadUserRating = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('story_ratings')
        .select('rating')
        .eq('story_id', story.id)
        .eq('user_id', user.id)
        .maybeSingle();

      if (!error && data) {
        setUserRating(data.rating);
      }
    };

    if (isCompleted) {
      loadUserRating();
    }
  }, [story.id, isCompleted]);

  // In the JSX, change this line:
  // <p className="text-sm text-white/40">{t('dashboard.startedTime', { time: formattedDate })}</p>
  
  // To this alternative approach that works around the type issue:
  const startedText = t('dashboard.startedTime').replace('{{time}}', formattedDate);

  // Add unique ID for dialog description
  const dialogDescriptionId = `upgrade-dialog-description-${story.id}`;

  return (
    <Card
      className="bg-white/5 backdrop-blur-sm border-white/10 hover:bg-white/10 transition-colors group relative"
    >
      <div
        className="absolute inset-0 cursor-pointer"
        onClick={() => onStoryClick(story.id, isCompleted)}
      />
      <CardHeader>
        <div className="flex justify-between items-start">
          <div className="space-y-1">
            <CardTitle className="text-white group-hover:text-white/90 transition-colors">
              {story.title}
            </CardTitle>
            {story.is_public && (
              <p className="text-sm text-white/60">
                {t('dashboard.by')} {story.author || t('dashboard.anonymous')}
              </p>
            )}
          </div>
          {isOwner && (
            <div className="flex gap-2">
              {isCompleted && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="relative z-10"
                        onClick={(e) => {
                          e.stopPropagation();
                          togglePublic(e);
                        }}
                      >
                        <Globe 
                          className={`h-4 w-4 ${isPublic ? 'text-emerald-400' : 'text-white/60'} hover:text-white/90`}
                        />
                        {!isFeatureEnabled('story_features', 'make_public') && (
                          <Lock className="h-3 w-3 absolute top-0 right-0 text-primary" />
                        )}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      {isFeatureEnabled('story_features', 'make_public')
                        ? (isPublic ? t('dashboard.makePrivate') : t('dashboard.makePublic'))
                        : t('dashboard.upgradeToMakePublic')}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              <AlertDialog open={isOpen} onOpenChange={setIsOpen}>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="relative z-10 hover:bg-white/10"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsOpen(true);
                    }}
                  >
                    <Trash2 className="h-4 w-4 text-white/60 hover:text-white/90" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>{t('dashboard.deleteStory.confirmTitle')}</AlertDialogTitle>
                    <AlertDialogDescription>
                      {t('dashboard.deleteStory.confirmMessage')}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel onClick={() => setIsOpen(false)}>
                      {t('cancel')}
                    </AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleDelete}
                      className="bg-red-500 hover:bg-red-600"
                    >
                      {t('delete')}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          <p className="text-white/60">{story.genre.map(genre => t(`storySetup.genres.${genre}`)).join(', ')}</p>
          <p className="text-sm text-white/40">{startedText}</p>
          {story.finished_at && (
            <div className="flex items-center gap-2">
              <p className="text-sm text-emerald-400">{t('dashboard.completed')}</p>
              {isPublic && (
                <span className="text-sm text-white/40">({t('dashboard.public')})</span>
              )}
            </div>
          )}
          {isCompleted && (
            <div className="flex items-center gap-2 relative z-10" onClick={(e) => e.stopPropagation()}>
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  onClick={() => handleRate(star)}
                  className="p-1 hover:scale-110 transition-transform"
                  disabled={isRating}
                >
                  {star <= (userRating || 0) ? (
                    <StarIcon className="h-4 w-4 text-yellow-400 fill-yellow-400" />
                  ) : (
                    <Star className="h-4 w-4 text-white/40" />
                  )}
                </button>
              ))}
              {story.average_rating && (
                <span className="text-sm text-white/60 ml-2">
                  ({story.average_rating.toFixed(1)})
                </span>
              )}
            </div>
          )}
        </div>
      </CardContent>
      <Dialog open={isUpgradeDialogOpen} onOpenChange={setIsUpgradeDialogOpen}>
        <DialogContent className="sm:max-w-md" aria-describedby={dialogDescriptionId}>
          <DialogHeader>
            <DialogTitle>{t('dashboard.upgrade.required')}</DialogTitle>
            <DialogDescription id={dialogDescriptionId}>
              {t('dashboard.upgrade.description')}
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2">
            <Lock className="w-5 h-5 text-primary" />
            <span>{t('dashboard.upgrade.shareStories')}</span>
          </div>
          <DialogFooter className="sm:justify-start">
            <Button
              type="button"
              onClick={() => {
                setIsUpgradeDialogOpen(false);
                navigate("/dashboard/more");
              }}
              className="w-full sm:w-auto"
            >
              {t('dashboard.upgrade.now')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default StoryCard;
